/**
 * Account Service
 * Provides enterprise-grade account management with caching, data conversion, and error handling
 */
import { accountApi } from './api';
import { Account } from '../types';

// Backend account type (matches backend model)
export interface BackendAccount {
  id: string;
  organizationId: string;
  name: string;
  type: 'CHECKING' | 'SAVINGS' | 'INVESTMENT' | 'CREDIT';
  institution: string;
  balance: number;
  runningTotal: number;
  transactionCount: number;
  currency: string;
  isActive: boolean;
  plaidAccountId?: string;
  monoAccountId?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  lastUpdated: Date | string;
}

// Frontend account type for components (cleaner interface)
export interface FrontendAccount extends Account {}

// Create/Update input types
export interface CreateAccountInput {
  name: string;
  type: 'checking' | 'savings' | 'investment' | 'credit';
  institution: string;
  balance: number;
  currency: string;
  plaidAccountId?: string;
  monoAccountId?: string;
}

export interface UpdateAccountInput {
  name?: string;
  type?: 'checking' | 'savings' | 'investment' | 'credit';
  institution?: string;
  balance?: number;
  currency?: string;
  isActive?: boolean;
  plaidAccountId?: string;
  monoAccountId?: string;
}

// Filter type
export interface AccountFilters {
  type?: 'checking' | 'savings' | 'investment' | 'credit';
  currency?: string;
  isActive?: boolean;
  institution?: string;
}

// Service response types
export interface AccountServiceResponse {
  accounts: FrontendAccount[];
  totalCount: number;
  lastSyncTimestamp?: Date;
}

// Cache interface
interface CacheEntry {
  data: FrontendAccount[];
  timestamp: Date;
  totalCount: number;
}

// Cache performance metrics
interface CacheMetrics {
  hitCount: number;
  missCount: number;
  validationCount: number;
  lastCacheTime?: Date;
  cacheAge?: number; // in minutes
}

// Cache health status
interface CacheHealth {
  status: 'fresh' | 'stale' | 'expired' | 'empty';
  age: number; // in minutes
  size: number;
  isValid: boolean;
}

/**
 * Account Service Class
 * Provides cached account operations with data format conversion
 */
class AccountService {
  private cache: CacheEntry | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private metrics: CacheMetrics = {
    hitCount: 0,
    missCount: 0,
    validationCount: 0
  };
  private lastSyncTimestamp: Date | null = null;

  /**
   * Data Conversion: Backend → Frontend
   */
  private convertToFrontendFormat(backendAccount: BackendAccount): FrontendAccount {
    return {
      id: backendAccount.id,
      name: backendAccount.name,
      type: backendAccount.type.toLowerCase() as 'checking' | 'savings' | 'investment' | 'credit',
      institution: backendAccount.institution,
      balance: backendAccount.balance,
      runningTotal: backendAccount.runningTotal,
      transactionCount: backendAccount.transactionCount,
      currency: backendAccount.currency,
      isActive: backendAccount.isActive,
      lastUpdated: new Date(backendAccount.updatedAt || backendAccount.lastUpdated)
    };
  }

  /**
   * Data Conversion: Frontend → Backend
   */
  private convertToBackendFormat(frontendAccount: CreateAccountInput | UpdateAccountInput) {
    const result: any = {
      ...frontendAccount
    };
    
    // Convert type to uppercase for backend
    if (result.type) {
      result.type = result.type.toUpperCase();
    }
    
    return result;
  }

  /**
   * Check if cache is valid
   */
  private isCacheValid(): boolean {
    if (!this.cache) return false;
    const age = Date.now() - this.cache.timestamp.getTime();
    return age < this.CACHE_DURATION;
  }

  /**
   * Get cache age in minutes
   */
  private getCacheAge(): number {
    if (!this.cache) return 0;
    return Math.floor((Date.now() - this.cache.timestamp.getTime()) / (1000 * 60));
  }

  /**
   * Update cache with new data
   */
  private updateCache(accounts: FrontendAccount[], totalCount: number): void {
    this.cache = {
      data: accounts,
      timestamp: new Date(),
      totalCount
    };
    this.metrics.lastCacheTime = new Date();
    this.lastSyncTimestamp = new Date();
  }

  /**
   * Invalidate cache
   */
  private invalidateCache(): void {
    this.cache = null;
  }

  /**
   * Get all accounts with caching
   */
  async getAll(filters?: AccountFilters): Promise<FrontendAccount[]> {
    try {
      // Check cache first (only for unfiltered requests)
      if (!filters && this.isCacheValid()) {
        this.metrics.hitCount++;
        console.debug('Account cache hit');
        return this.cache!.data;
      }

      this.metrics.missCount++;
      console.debug('Account cache miss - fetching from backend');

      // Fetch from backend
      const backendAccounts = await accountApi.getAll(filters ? this.convertToBackendFormat(filters) : undefined);
      
      // Convert to frontend format
      const frontendAccounts = backendAccounts.map(account => this.convertToFrontendFormat(account));
      
      // Update cache only for unfiltered requests
      if (!filters) {
        this.updateCache(frontendAccounts, frontendAccounts.length);
      }
      
      return frontendAccounts;
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      throw error;
    }
  }

  /**
   * Get account by ID
   */
  async getById(id: string): Promise<FrontendAccount | null> {
    try {
      // Check cache first
      if (this.isCacheValid()) {
        const cachedAccount = this.cache!.data.find(account => account.id === id);
        if (cachedAccount) {
          this.metrics.hitCount++;
          return cachedAccount;
        }
      }

      this.metrics.missCount++;
      const backendAccount = await accountApi.getById(id);
      
      if (!backendAccount) {
        return null;
      }
      
      return this.convertToFrontendFormat(backendAccount);
    } catch (error) {
      console.error(`Failed to fetch account ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new account
   */
  async create(accountData: CreateAccountInput): Promise<FrontendAccount> {
    try {
      const backendData = this.convertToBackendFormat(accountData);
      const backendAccount = await accountApi.create(backendData);
      const frontendAccount = this.convertToFrontendFormat(backendAccount);
      
      // Invalidate cache to force refresh
      this.invalidateCache();
      
      return frontendAccount;
    } catch (error) {
      console.error('Failed to create account:', error);
      throw error;
    }
  }

  /**
   * Update existing account
   */
  async update(id: string, updateData: UpdateAccountInput): Promise<FrontendAccount> {
    try {
      const backendData = this.convertToBackendFormat(updateData);
      const backendAccount = await accountApi.update(id, backendData);
      const frontendAccount = this.convertToFrontendFormat(backendAccount);
      
      // Invalidate cache to force refresh
      this.invalidateCache();
      
      return frontendAccount;
    } catch (error) {
      console.error(`Failed to update account ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete account
   */
  async delete(id: string): Promise<void> {
    try {
      await accountApi.delete(id);
      
      // Invalidate cache to force refresh
      this.invalidateCache();
    } catch (error) {
      console.error(`Failed to delete account ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get account count
   */
  async getCount(filters?: AccountFilters): Promise<number> {
    try {
      // Check cache for unfiltered count
      if (!filters && this.isCacheValid()) {
        this.metrics.hitCount++;
        return this.cache!.totalCount;
      }

      this.metrics.missCount++;
      const backendFilters = filters ? this.convertToBackendFormat(filters) : undefined;
      return await accountApi.getCount(backendFilters);
    } catch (error) {
      console.error('Failed to get account count:', error);
      throw error;
    }
  }

  /**
   * Check if account exists
   */
  async checkExists(name: string, institution: string): Promise<boolean> {
    try {
      return await accountApi.checkExists(name, institution);
    } catch (error) {
      console.error('Failed to check account existence:', error);
      throw error;
    }
  }

  /**
   * Force refresh accounts from backend
   */
  async refresh(): Promise<FrontendAccount[]> {
    this.invalidateCache();
    return this.getAll();
  }

  /**
   * Warm cache in background
   */
  async warmCache(): Promise<void> {
    try {
      console.debug('Warming account cache...');
      await this.getAll();
    } catch (error) {
      console.warn('Failed to warm account cache:', error);
    }
  }

  /**
   * Get cache performance metrics
   */
  getCacheMetrics(): CacheMetrics & { hitRate: number } {
    const totalRequests = this.metrics.hitCount + this.metrics.missCount;
    const hitRate = totalRequests > 0 ? Math.round((this.metrics.hitCount / totalRequests) * 100) : 0;
    
    return {
      ...this.metrics,
      hitRate,
      cacheAge: this.getCacheAge()
    };
  }

  /**
   * Get cache health status
   */
  getCacheHealth(): CacheHealth {
    const age = this.getCacheAge();
    const size = this.cache?.data.length || 0;
    
    let status: CacheHealth['status'];
    if (!this.cache) {
      status = 'empty';
    } else if (age < 2) {
      status = 'fresh';
    } else if (age < 5) {
      status = 'stale';
    } else {
      status = 'expired';
    }

    return {
      status,
      age,
      size,
      isValid: this.isCacheValid()
    };
  }

  /**
   * Reset cache performance metrics
   */
  resetCacheMetrics(): void {
    this.metrics = {
      hitCount: 0,
      missCount: 0,
      validationCount: 0
    };
  }

  /**
   * Get last sync timestamp
   */
  getLastSyncTimestamp(): Date | null {
    return this.lastSyncTimestamp;
  }

  /**
   * Validate cache integrity
   */
  async validateCache(): Promise<{ isValid: boolean; issues: string[] }> {
    this.metrics.validationCount++;
    const issues: string[] = [];
    
    if (!this.cache) {
      return { isValid: true, issues: [] }; // Empty cache is valid
    }

    // Check cache age
    const age = this.getCacheAge();
    if (age > 10) {
      issues.push(`Cache is very old (${age} minutes)`);
    }

    // Check data integrity
    if (!Array.isArray(this.cache.data)) {
      issues.push('Cache data is not an array');
    }

    // Check account objects
    for (const account of this.cache.data) {
      if (!account.id || !account.name || !account.type) {
        issues.push(`Invalid account object: ${account.id || 'unknown'}`);
      }
    }

    return {
      isValid: issues.length === 0,
      issues
    };
  }
}

// Export singleton instance
const accountService = new AccountService();
export default accountService;